import { useEffect } from 'react';
import { useAuthTokenContext } from '../../providers/AuthTokenContext';
import { useApolloClientsContext } from '../../providers/ApolloClientsProvider';
import { ConfigContext } from '../../providers/ConfigContext';
import RECORD_SIGN_IN from '../../graphql/community/graphql/recordSignIn';
import { sessionIdentity } from '../../authentication/sessionIdentity';

// Reporting is observational: never blocks rendering, roles, refresh or logout.
export const SignInReporter = () => {
  const { authSessionKey, getToken } = useAuthTokenContext();
  const { clients } = useApolloClientsContext();
  const config = ConfigContext.useContainer();
  const enabled = config?.loginStatistics?.enabled === true;
  const client = clients.community;

  useEffect(() => {
    if (!enabled || !authSessionKey || !client) return;
    let cancelled = false;
    let accepted = false;
    let running = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const report = async () => {
      if (
        cancelled ||
        accepted ||
        running ||
        sessionIdentity(getToken()) !== authSessionKey
      )
        return;
      running = true;
      try {
        const result = await client.mutate<{
          recordSignIn: { accepted: boolean };
        }>({
          mutation: RECORD_SIGN_IN,
          fetchPolicy: 'no-cache',
        });
        if (!cancelled && result.data?.recordSignIn.accepted) accepted = true;
      } catch {
        // Authentication remains independent. A later report can safely replay.
      } finally {
        running = false;
        if (!cancelled && !accepted) {
          timer = setTimeout(report, Math.min(1000 * 2 ** Math.min(attempts++, 6), 60000));
        }
      }
    };
    const retry = () => {
      if (timer) clearTimeout(timer);
      attempts = 0;
      void report();
    };
    void report();
    window.addEventListener('online', retry);
    window.addEventListener('focus', retry);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('online', retry);
      window.removeEventListener('focus', retry);
    };
  }, [enabled, authSessionKey, client, getToken]);
  return null;
};
